import Logger, { LEVEL } from '../common/logger';
import { isBrowserSupported } from '../common/browser';
import { CommonConfig } from '../common/config';
import DOM from '../common/DOM';
import IPC from './helpers/ipc';
import TrackerApi from './genesys/TrackerApi';
import * as IFrame from './helpers/iframe';
import { MAIN_ACTIONS, DISPLAY } from '../child/components/main/types';
import { WIDGET_ACTIONS, COOKIES } from '../child/components/layers/widget/types';
import defaultOptions from '../child/helpers/options/defaults';
import Scripts from '../common/scripts';
import Features from './features';
import GenesysTools from '../common/GenesysTools';
import CookiesManager from './helpers/CookiesManager';
import Conditions from './helpers/Conditions';
import { POPUP_ACTIONS } from '../child/components/layers/popup/types';
import { isUserMOLoggedIn } from './helpers/userMOLoggedIn';

export class OPLWCParent {
    constructor(settings) {
        Logger.setDomain('WC PARENT');
        this.settings = settings;
        this.tracker = new TrackerApi();
        this.ignoreInvites = false;

        if (this.settings.env.singlePage) {
            IPC.setSinglePage();
        }

        this.state = { current: '', next: '' };
        this.stateInterval = null;
        this.onCloseCallbacks = [];
        this.onDownloadCallbacks = [];
        this.onMessageReceivedCallbacks = [];
        this.isOpen = false;

        this.init();
        this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    version() {
        return process.env.WC_VERSION;
    }

    debug(enable) {
        Logger.enable(enable);
        IPC.sendEvent(WIDGET_ACTIONS.DEBUG, enable);
    }

    showIframeBorder(show = true) {
        IFrame.showIframeBorder(show);
    }

    init() {
        const enableDebug = () => {
            if (window.location.href.match(/wcd=1/)) {
                this.debug(true);
            }
            let store = sessionStorage.getItem('oplwidget.state.chat');
            if (store && store.length > 0) {
                store = JSON.parse(store);
                Logger.setDebugGWE(
                    (store.config && store.config.widget && store.config.widget.debugGWE) || false
                );
            }
        };
        enableDebug();

        Logger.write(
            LEVEL.INFO,
            `Version: ${process.env.WC_VERSION}`,
            'background-color: green; color: white;'
        );

        DOM.onWindowLoad(() => {
            Logger.log('[app:init] DOM ready. Starting...');
            this.attachButtons();

            //--Attach onMessage
            this.messageHandler = this.messageHandler.bind(this);
            IPC.onEvent(this.messageHandler);

            //--Attach onResize
            window.addEventListener('resize', IFrame.onWindowResize, false);

            // Load child
            if (!this.settings.env.singlePage) {
                Logger.log('[app:init] adding iframe');
                document.addEventListener('click', this.onDocumentClick, false);
                document.addEventListener('touchstart', this.onDocumentClick, false);

                IFrame.createIFrame({
                    id: CommonConfig.iFrameId,
                    src:
                        this.settings.env && this.settings.env.iFrameURL
                            ? this.settings.env.iFrameURL
                            : CommonConfig.iFrameSrc
                });
            } else {
                Logger.log('[app:init] running as single page mode');
            }

            if (this.settings.env && this.settings.env.literalsURL) {
                Scripts.loadJSON({
                    url: this.settings.env.literalsURL,
                    successCallback: (result) => {
                        IPC.sendEvent(MAIN_ACTIONS.LOAD_SETTINGS, {
                            ...(this.settings || {}),
                            literals: result
                        });
                        IPC.sendEvent(MAIN_ACTIONS.INIT);
                        if (isUserMOLoggedIn(document.referrer, window.location.href)) {
                            IPC.sendEvent(WIDGET_ACTIONS.SEND_USER_MO_LOGGED_IN_TO_CHAT);
                        }
                    },
                    errorCallback: () => {
                        Logger.error('[app:init] error loading literals.');
                    }
                });
            } else if (this.settings && this.settings.literals) {
                IPC.sendEvent(MAIN_ACTIONS.LOAD_SETTINGS, {
                    ...(this.settings || {}),
                    literals: this.settings.literals
                });
            }

            if (this.settings && this.settings.features) {
                Features.init(
                    this.settings.features,
                    window.location.origin + window.location.pathname
                );
            }
        });
    }

    messageHandler(message) {
        switch (message.command) {
            case MAIN_ACTIONS.OPEN:
                document.dispatchEvent(new CustomEvent('wc.onopen'));
                break;

            case MAIN_ACTIONS.OPEN_URL:
                window.open(
                    message.data.url,
                    message.data.self ? '_self' : message.data.blank ? '_blank' : ''
                );
                break;

            case MAIN_ACTIONS.CLOSED:
                {
                    this.clearInviteRequest();
                    this.enableButtons();

                    if (this.stateInterval) {
                        clearInterval(this.stateInterval);
                        this.stateInterval = null;
                        this.state = { current: '', next: '' };
                    }

                    if (message.data && message.data.view === 'PRELOADER') {
                        this.ignoreInvites = true;
                    }

                    this.onCloseCallbacks.forEach((callback) => {
                        try {
                            callback(message.data.reason || undefined);
                        } catch (error) {
                            Logger.warn(
                                '[app:messageHandler] Error handling onClose callback: ' + error
                            );
                        }
                    });

                    IFrame.resizeIFrame({
                        height: 0,
                        width: 0,
                        mode: DISPLAY.NONE
                    });
                    IFrame.returnFocusFromIFrame();

                    document.dispatchEvent(
                        new CustomEvent('wc.onclose', {
                            detail: {
                                reason: message.data.reason || undefined
                            }
                        })
                    );
                }
                break;

            case MAIN_ACTIONS.SET_PARENT_IFRAME_MODE:
                if (
                    this.settings.widget &&
                    this.settings.widget.fullscreen &&
                    !this.settings.env.singlePage
                ) {
                    // ignore size changes, force full screen
                    const iframe = document.getElementById(CommonConfig.iFrameId);
                    iframe.style.cssText =
                        'position: fixed; left: 0px; top: 0px; width: 100%; height: 100%; z-index: 999999;';
                    return;
                }
                IFrame.resizeIFrame(message.data);
                break;

            case WIDGET_ACTIONS.CHAT_MESSAGERECEIVED:
                this.onMessageReceivedCallbacks.forEach((callback) => {
                    try {
                        callback(message.data || undefined);
                    } catch (error) {
                        Logger.warn(
                            '[app:messageHandler] Error handling onMessageReceived callback: ' +
                                error
                        );
                    }
                });
                break;

            case WIDGET_ACTIONS.CHAT_DOWNLOAD:
                this.onDownloadCallbacks.forEach((callback) => {
                    try {
                        callback(message.data || undefined);
                    } catch (error) {
                        Logger.warn(
                            '[app:messageHandler] Error handling onDownload callback: ' + error
                        );
                    }
                });
                break;

            case WIDGET_ACTIONS.CALL:
                window.open('tel:' + message.data, '_self');
                break;

            case WIDGET_ACTIONS.REDIRECT:
                window.location.href = message.data;
                break;

            case WIDGET_ACTIONS.OPEN_POPUP:
                this.openPopup(message.data.payload.id);
                break;

            case WIDGET_ACTIONS.MINIMIZED:
                document.dispatchEvent(new CustomEvent('wc.onminimize'));
                break;

            case WIDGET_ACTIONS.MAXIMIZED:
                document.dispatchEvent(new CustomEvent('wc.onmaximize'));
                break;

            case COOKIES.START_CHAT:
                CookiesManager.onStartChat();
                break;

            case COOKIES.STOP_CHAT:
                CookiesManager.onStopChat();
                break;

            case COOKIES.START_QUESTIONNAIRE:
                CookiesManager.onStartQuestionnaire();
                break;

            case COOKIES.CHECK_USABILA:
                CookiesManager.checkUsabilaCookie();
                break;

            case POPUP_ACTIONS.LOAD_CONFIG:
                this.loadConfig(message.data);
                break;

            case COOKIES.SET_DISABLING_PROACTIVE_COOOKIE:
                CookiesManager.setDisablingProactiveCookie();
                break;

            default:
                break;
        }
    }

    getConfig() {
        return GenesysTools.getInvite();
    }

    /********************* Invite handler ********************* */

    /**
     * Start Widget by invite.
     */
    start(view = null) {
        try {
            this.clearInviteRequest();

            Logger.log('[app:start] widget invited');
            this.tracker.sendEvent({ event: 'widgetStarting' });

            if (this.ignoreInvites) {
                Logger.log('[app:start] ignoring invite after timeout');
                return;
            }

            const isInvitedByClick = () => (this.inviteTimeout ? true : false);
            if (isInvitedByClick() === false && Conditions.showWidget() === false) {
                Logger.log('[app:start] disabling start by cookie');
                return;
            }

            const config = this.getConfig();
            if (
                window.WC &&
                window.WC.chatProvider &&
                window.WC.chatProvider === 'Closer' &&
                config.defaultView === 'chat'
            ) {
                const genesysQueue = config.chatUserData
                    ? config.chatUserData.category
                        ? config.chatUserData.category
                        : ''
                    : '';

                window.WC.Closer.open({ tags: [genesysQueue] });
                this.close(true, 'Closer chat open');
            } else {
                this.open({ config, view });
            }
        } catch (error) {
            Logger.error(`OPLWCParent.start: ${error}`);
            this.tracker.sendEvent({
                event: 'widgetReportError',
                data: {
                    errorType: 'startException',
                    errorEvent: 'EngagementAttempt',
                    errorInfo: error,
                    errorLayerMode: 'OPLWCParent'
                }
            });
        }
    }

    open({ config, view }) {
        const configureQuestionnaire = (config) => {
            return new Promise((resolve) => {
                if (config && config.questionnaireOptions) {
                    Features.run('questionnaire-defs', 'mergeConfigs', {
                        questionnaireOptions: config.questionnaireOptions,
                        onSuccess: (newQuestionnaireOptions) => {
                            config.questionnaireOptions = newQuestionnaireOptions;
                            resolve(config);
                        }
                    });
                } else {
                    resolve(config);
                }
            });
        };

        Logger.setDebugGWE((config && config.debugGWE) || false);

        configureQuestionnaire(config).then((config) => {
            const documentSize = DOM.getDocumentSize();

            if (config) {
                if (config.eipView && config.eipOptions) {
                    Features.start('exit-intent', config.eipOptions, {
                        onOpen: (trigger) => {
                            IPC.sendEvent(MAIN_ACTIONS.OPEN, {
                                config: {
                                    ...config,
                                    defaultView: config.eipView,
                                    cmbUserData: {
                                        ...config.cmbUserData,
                                        source: trigger
                                    }
                                },
                                parentWidth: documentSize.width,
                                parentHeight: documentSize.height,
                                location: window.location.href,
                                settings: this.settings || {},
                                trigger: trigger
                            });
                        }
                    });
                }

                if (!config.defaultView) {
                    return;
                }
            }

            if (config || view) {
                if (config && config.debug) {
                    this.debug(true);
                }

                IPC.sendEvent(MAIN_ACTIONS.OPEN, {
                    // temporary=true allows to reload config on already running widget (eg widget opened without configuration)
                    config: config || { defaultView: view, temporary: true },
                    parentWidth: documentSize.width,
                    parentHeight: documentSize.height,
                    location: window.location.href,
                    settings: this.settings || {}
                });
            } else {
                Logger.warn('[app:start] no invite data object');
                this.tracker.sendEvent({ event: 'widgetNoInviteDataObject' });
            }
        });
    }

    openPopup(configId) {
        if (!configId) {
            return;
        }

        IPC.sendEvent(POPUP_ACTIONS.OPEN, {
            configId: configId
        });
    }

    loadConfig(config) {
        Logger.setDebugGWE((config && config.debugGWE) || false);

        const configureQuestionnaire = (config) => {
            return new Promise((resolve) => {
                if (config && config.questionnaireOptions) {
                    Features.run('questionnaire-defs', 'mergeConfigs', {
                        questionnaireOptions: config.questionnaireOptions,
                        onSuccess: (newQuestionnaireOptions) => {
                            config.questionnaireOptions = newQuestionnaireOptions;
                            resolve(config);
                        }
                    });
                } else {
                    resolve(config);
                }
            });
        };

        configureQuestionnaire(config).then((config) => {
            const documentSize = DOM.getDocumentSize();

            if (config) {
                if (config.debug) {
                    this.debug(true);
                }

                if (!config.defaultView) {
                    return;
                }

                IPC.sendEvent(MAIN_ACTIONS.LOAD_CONFIG, {
                    config: config,
                    parentWidth: documentSize.width,
                    parentHeight: documentSize.height,
                    location: window.location.href,
                    settings: this.settings || {}
                });
            } else {
                Logger.warn('Start: no invite data object');
            }
        });
    }

    close(force = false, reason = undefined) {
        Logger.log(`[app:close] close widget [force=${force} reason=${reason}]`);
        IPC.sendEvent(WIDGET_ACTIONS.CLOSE, { force, reason });
    }

    onClose(callback) {
        this.onCloseCallbacks.push(callback);
    }

    onDownload(callback) {
        this.onDownloadCallbacks.push(callback);
    }

    getCurrentState(callback) {
        if (callback === undefined) {
            // old, synchronous mode (simulated on timer). Migrate 'Mój Orange' app to callback style to remove this ugly code.
            // Refresh state every 1 second.
            if (!this.stateInterval) {
                this.stateInterval = setInterval(() => {
                    IPC.getCurrentState().then((state) => (this.state = state));
                }, 1000);
            }
            return this.state;
        } else {
            // new, callback style
            IPC.getCurrentState().then(callback);
        }
    }

    isChatSessionActive(callback) {
        if (callback) {
            if (window.localStorage) {
                const wid = localStorage.getItem('oplwidget.state.chat');
                callback(wid !== null);
            } else {
                callback(false);
            }
        }
    }

    onMessageReceived(callback) {
        this.onMessageReceivedCallbacks.push(callback);
    }

    /********************* Document click handler ********************* */

    onDocumentClick(e) {
        let supervisedElementClicked = false;
        if (this.settings.onDemand) {
            this.settings.onDemand.elements.forEach((element) => {
                const buttons = document.querySelectorAll(element.selector);
                buttons.forEach((button) => {
                    if (e.target === button) supervisedElementClicked = true;
                }, this);
            }, this);
        }
        if (!supervisedElementClicked) {
            IPC.sendEvent(WIDGET_ACTIONS.DOCUMENT_CLICK);
        }
    }

    /********************* Custom buttons handler ********************* */

    attachButtons() {
        if (this.settings.onDemand) {
            const inviteButtonHandler = (e) => {
                Logger.log(`[app:start] user button clicked: ${e.target.innerText}`);

                let target = e.target;
                while (target && target.dataset.userevent === undefined) {
                    target = target.parentElement;
                }

                if (target) {
                    const args = {};
                    if (target.dataset.attributesToSend !== 'undefined') {
                        const attributes = target.dataset.attributesToSend.split(',');
                        attributes.forEach((a) => {
                            args[a] = target[a]
                                ? target[a]
                                : target.dataset[a]
                                ? target.dataset[a]
                                : '';
                        });
                    }

                    if (this.isOpen && target.dataset.preloaderType !== 'popup') {
                        Logger.log('[app:start] Widget is already open');
                        IPC.sendEvent(WIDGET_ACTIONS.MAXIMIZE);
                        return;
                    }
                    this.disableButtons();

                    this.requestInvite(
                        target.dataset.userevent,
                        target.dataset.preloaderType,
                        Object.keys(args).length !== 0 ? args : null
                    );

                    IFrame.setFocusToIFrame();
                }
            };

            const attachButtonEvents = () => {
                let attached = false;
                this.settings.onDemand.elements.forEach((element) => {
                    const buttons = document.querySelectorAll(element.selector);
                    buttons.forEach((button) => {
                        if (!button.dataset.userevent) {
                            Logger.log(
                                `[app:start] attaching to user button: ${button.innerText} via ${element.selector}`
                            );
                            button.dataset.userevent = element.eventName;
                            button.dataset.preloaderType = element.preloaderType
                                ? element.preloaderType
                                : null;
                            button.dataset.selector = element.selector;
                            button.dataset.attributesToSend = element.attributesToSend;
                            button.addEventListener('click', inviteButtonHandler, false);
                            attached = true;
                        }
                    }, this);
                }, this);

                if (attached) {
                    IPC.getCurrentState().then(({ current }) => {
                        if (current !== '') {
                            this.disableButtons();
                        }
                    });
                }
            };

            // try to attach to buttons. We need to wait for a while for full page init by require.js or other async stuff (eg. modals)
            const retries = this.settings.onDemand.retries || [
                1, 500, 1000, 2000, 3500, 6000, 10000
            ];
            retries.forEach((timeout) => {
                Logger.log(`[app:start] try attachButtons - ${timeout}`);
                setTimeout(attachButtonEvents, timeout);
            });

            // after above retries try to attach every 5 seconds... forever.
            if (this.attachInterval) {
                clearInterval(this.attachInterval);
                this.attachInterval = null;
            }
            setTimeout(() => {
                Logger.log('[app:start] try attachButtons forever');
                this.attachInterval = setInterval(attachButtonEvents, 5000);
            }, 15000);
        }
    }

    enableButtons() {
        Logger.log('[app:start] enable user buttons');
        this.isOpen = false;
    }

    disableButtons() {
        Logger.log('[app:start] disable user buttons');
        this.isOpen = true;
    }

    openChatHandler(args) {
        Logger.log('[app:start] user button clicked');

        if (!args) {
            args = {
                contactProcessType: 'Support'
            };
        }

        let userEvent = '';
        let preloaderType = undefined;
        switch (args.contactProcessType) {
            case 'Sale':
                userEvent = 'UserData2';
                break;
            case 'Support':
            default:
                userEvent = 'UserData1';
                break;
        }

        if (this.isOpen && preloaderType !== 'popup') {
            Logger.log('[app:start] Widget is already open');
            IPC.sendEvent(WIDGET_ACTIONS.MAXIMIZE);
            return;
        }
        this.disableButtons();
        this.requestInvite(userEvent, preloaderType, null);
        IFrame.setFocusToIFrame();
    }

    requestInvite(
        userEvent = this.settings.onDemand.userEvent || null,
        preloaderType = undefined,
        args = null
    ) {
        Logger.log(`[app:start] request invite (preloader = ${preloaderType})`);
        this.ignoreInvites = false;

        if (!userEvent) {
            return;
        }

        const sendInvite = () => {
            let payload = { event: userEvent };
            if (args) {
                let mappedargs = {};
                if ((args['view_type'] || null) === 'chat') {
                    // map fields for chat
                    Object.keys(args).forEach((field) => {
                        const key = defaultOptions.chatOptions.registration.mapping[field] || field;
                        mappedargs[key] = args[field];
                    });
                } else {
                    // not chat, leave as is
                    mappedargs = args;
                }

                payload = { ...payload, data: mappedargs };
            }
            this.tracker.sendEvent(payload);

            if (preloaderType === undefined || preloaderType.toUpperCase() !== 'NONE') {
                const documentSize = DOM.getDocumentSize();

                IPC.sendEvent(MAIN_ACTIONS.OPEN, {
                    config: {
                        defaultView: preloaderType === 'popup' ? 'preloaderPopup' : 'preloader',
                        preloadOptions: {
                            icon: 'spinner',
                            message:
                                this.settings.onDemand && this.settings.onDemand.waitMessage
                                    ? this.settings.onDemand.waitMessage
                                    : 'Oczekiwanie na zestawienie połączenia...'
                        }
                    },
                    parentWidth: documentSize.width,
                    parentHeight: documentSize.height,
                    location: window.location.href,
                    settings: this.settings || {}
                });

                this.clearInviteRequest();
                this.inviteTimeout = setTimeout(() => {
                    Logger.error('[app:start] request invite timeout');
                    this.tracker.sendEvent({ event: 'widgetRequestInviteTimeout' });
                    this.ignoreInvites = true;
                    IPC.sendEvent(MAIN_ACTIONS.OPEN, {
                        config: {
                            defaultView: preloaderType === 'popup' ? 'preloaderPopup' : 'preloader',
                            preloadOptions: {
                                icon: 'exclamation',
                                message:
                                    this.settings.onDemand && this.settings.onDemand.timeoutMessage
                                        ? this.settings.onDemand.timeoutMessage
                                        : 'Nasz serwis jest chwilowo niedostępny. Przepraszamy, spróbuj ponownie później.'
                            }
                        }
                    });
                }, (this.settings.onDemand ? this.settings.onDemand.timeout || 30 : 30) * 1000);
            }
        };

        sendInvite();
    }

    clearInviteRequest() {
        if (this.inviteTimeout) {
            clearTimeout(this.inviteTimeout);
            this.inviteTimeout = null;
        }
    }

    sendOrderIdToChat(orderId) {
        if (!orderId) {
            return;
        }
        IPC.sendEvent(WIDGET_ACTIONS.BROADCAST_ORDER_ID_TO_CHAT, orderId);
    }
}

(function () {
    if (!isBrowserSupported()) {
        Logger.error('[app:start] Unsupported browser!');
        return;
    }

    if (!window._wc) {
        Logger.error('[app:start] No window._wc configuration object!');
        return;
    }

    // make class public
    window.OPLWCParent = new OPLWCParent(window._wc);

    /** for old Widget compatibility only */
    window.OPLWidget = {
        ShowDefaultView: (view = null) => window.OPLWCParent.start(view),
        Init: () => {},

        // API for 'Mój Orange' app
        RequestChatView: (event = 'mobileChatStart') => {
            window.OPLWCParent.requestInvite(event, undefined, window._wc.startArguments);
        },
        CloseChatView: () => {
            window.OPLWCParent.close(true);
        },
        NextStepChatView: () => {
            window.OPLWCParent.close(false);
        },
        GetCurrentStage: (callback) => {
            return window.OPLWCParent.getCurrentState(callback);
        },
        OnClose: (callback) => {
            return window.OPLWCParent.onClose(callback);
        },
        OnMessageReceived: (callback) => {
            window.OPLWCParent.onMessageReceived(callback);
        },
        OnDownload: (callback) => {
            return window.OPLWCParent.onDownload(callback);
        }
    };
    window.OPLWidgetChat = { Init: () => {} };
    window.OPLWidgetInvite = {
        init: (c) => c()
    };

    window.WC = {
        ...window.WC,
        openChat: (args) => {
            window.WC.chatProvider = 'Genesys';
            window.OPLWCParent.openChatHandler(args);
        }
    };
})();
